// ═══════════════════════════════════════════════════════════
// ARAMS Dashboard — Landing Page (Public Marketing Page)
// Mind-blowing hero + features + how-it-works + CTA
// ═══════════════════════════════════════════════════════════

import { useRef, useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import {
  Sparkles,
  Shield,
  Zap,
  Bot,
  GitBranch,
  BarChart3,
  Eye,
  Users,
  CheckCircle2,
  ArrowRight,
  Play,
  ChevronDown,
  Globe,
  Lock,
  Cpu,
  Terminal,
  Layers,
  Rocket,
} from 'lucide-react';
import { Button, Badge } from '@/components/ui';

// ── Particle Canvas ──────────────────────────────────────

function ParticleField() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animId: number;
    const particles: {
      x: number;
      y: number;
      vx: number;
      vy: number;
      size: number;
      opacity: number;
    }[] = [];
    const PARTICLE_COUNT = 80;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    for (let i = 0; i < PARTICLE_COUNT; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.3,
        vy: (Math.random() - 0.5) * 0.3,
        size: Math.random() * 2 + 0.5,
        opacity: Math.random() * 0.5 + 0.1,
      });
    }

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Draw connections
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i]!.x - particles[j]!.x;
          const dy = particles[i]!.y - particles[j]!.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 150) {
            ctx.beginPath();
            ctx.strokeStyle = `hsla(228 100% 60% / ${0.08 * (1 - dist / 150)})`;
            ctx.lineWidth = 0.5;
            ctx.moveTo(particles[i]!.x, particles[i]!.y);
            ctx.lineTo(particles[j]!.x, particles[j]!.y);
            ctx.stroke();
          }
        }
      }

      // Draw particles
      for (const p of particles) {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > canvas.width) p.vx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.vy *= -1;

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = `hsla(228 100% 70% / ${p.opacity})`;
        ctx.fill();
      }

      animId = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return <canvas ref={canvasRef} className="absolute inset-0 z-0 pointer-events-none" />;
}

// ── Animated Counter ─────────────────────────────────────

function Counter({ value, suffix = '' }: { value: number; suffix?: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true });
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const duration = 2000;
    const startTime = performance.now();

    const tick = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      start = Math.floor(eased * value);
      setCount(start);
      if (progress < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [inView, value]);

  return (
    <span ref={ref}>
      {count}
      {suffix}
    </span>
  );
}

// ── Section Reveal ───────────────────────────────────────

function Reveal({
  children,
  className = '',
  delay = 0,
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

// ── Floating Badge ───────────────────────────────────────

function FloatingBadge({
  children,
  className,
  delay = 0,
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 0.35, scale: 1 }}
      transition={{ delay: 1.5 + delay, duration: 0.5, type: 'spring' }}
      className={`absolute z-0 pointer-events-none glass-card px-3 py-1.5 flex items-center gap-2 text-xs font-medium ${className}`}
    >
      {children}
    </motion.div>
  );
}

// ══════════════════════════════════════════════════════════
export default function LandingPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const heroRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll();
  const heroOpacity = useTransform(scrollYProgress, [0, 0.15], [1, 0]);
  const heroScale = useTransform(scrollYProgress, [0, 0.15], [1, 0.95]);

  const features = useMemo(
    () => [
      {
        icon: Bot,
        title: t('landing.feat1Title'),
        desc: t('landing.feat1Desc'),
        color: 'from-brand-500/20 to-purple-500/20',
        iconColor: 'text-brand-400',
      },
      {
        icon: GitBranch,
        title: t('landing.feat2Title'),
        desc: t('landing.feat2Desc'),
        color: 'from-emerald-500/20 to-teal-500/20',
        iconColor: 'text-emerald-400',
      },
      {
        icon: Users,
        title: t('landing.feat3Title'),
        desc: t('landing.feat3Desc'),
        color: 'from-orange-500/20 to-amber-500/20',
        iconColor: 'text-orange-400',
      },
      {
        icon: Eye,
        title: t('landing.feat4Title'),
        desc: t('landing.feat4Desc'),
        color: 'from-pink-500/20 to-rose-500/20',
        iconColor: 'text-pink-400',
      },
      {
        icon: Shield,
        title: t('landing.feat5Title'),
        desc: t('landing.feat5Desc'),
        color: 'from-red-500/20 to-orange-500/20',
        iconColor: 'text-red-400',
      },
      {
        icon: BarChart3,
        title: t('landing.feat6Title'),
        desc: t('landing.feat6Desc'),
        color: 'from-cyan-500/20 to-blue-500/20',
        iconColor: 'text-cyan-400',
      },
    ],
    [t],
  );

  const steps = useMemo(
    () => [
      { icon: GitBranch, title: t('landing.step1Title'), desc: t('landing.step1Desc') },
      { icon: Cpu, title: t('landing.step2Title'), desc: t('landing.step2Desc') },
      { icon: Terminal, title: t('landing.step3Title'), desc: t('landing.step3Desc') },
      { icon: BarChart3, title: t('landing.step4Title'), desc: t('landing.step4Desc') },
    ],
    [t],
  );

  const stats = useMemo(
    () => [
      { value: 18, suffix: '', label: t('landing.stat1') },
      { value: 11, suffix: '', label: t('landing.stat2') },
      { value: 95, suffix: '%', label: t('landing.stat3') },
      { value: 3, suffix: 's', label: t('landing.stat4') },
    ],
    [t],
  );

  const techStack = [
    { icon: Layers, label: 'Playwright' },
    { icon: Sparkles, label: 'Gemini AI' },
    { icon: Globe, label: 'React 19' },
    { icon: Lock, label: 'AES-256' },
    { icon: Zap, label: 'Express 5' },
    { icon: Rocket, label: 'Docker' },
  ];

  return (
    <div className="relative bg-surface-950 text-surface-200 overflow-x-hidden">
      {/* ═══ NAVBAR ═══════════════════════════════════════ */}
      <nav className="fixed top-0 left-0 right-0 z-50 border-b border-surface-800/50 backdrop-blur-xl bg-surface-950/70">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="/arams-logo-white.png"
              alt="ARAMS"
              className="w-8 h-8 rounded-lg object-contain"
            />
            <span className="font-bold text-lg gradient-text">{t('common.appName')}</span>
            <span className="hidden sm:inline text-xs text-surface-500 ml-1">
              {t('common.tagline')}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate('/login')} className="text-sm">
              {t('landing.signIn')}
            </Button>
            <Button onClick={() => navigate('/login')} className="text-sm px-5">
              {t('landing.getStarted')}
              <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </nav>

      {/* ═══ HERO ═════════════════════════════════════════ */}
      <section
        ref={heroRef}
        className="relative min-h-screen flex items-center justify-center pt-16 overflow-hidden"
      >
        <ParticleField />

        {/* Gradient orbs */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/3 left-1/4 w-[600px] h-[600px] bg-brand-600/15 rounded-full blur-[120px]" />
          <div className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] bg-purple-600/10 rounded-full blur-[100px]" />
          <div className="absolute top-1/2 right-1/3 w-[400px] h-[400px] bg-pink-600/8 rounded-full blur-[80px]" />
        </div>

        <motion.div
          style={{ opacity: heroOpacity, scale: heroScale }}
          className="relative z-10 text-center px-6 max-w-5xl mx-auto"
        >
          {/* Announcement badge */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="inline-flex items-center gap-2 glass-card px-4 py-2 mb-8 text-sm"
          >
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inset-0 rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
            </span>
            <span className="text-surface-300">{t('landing.badge')}</span>
          </motion.div>

          {/* Main heading */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-extrabold tracking-tight leading-[0.95] mb-6"
          >
            <span className="text-surface-100">{t('landing.heroLine1')}</span>
            <br />
            <span className="gradient-text">{t('landing.heroLine2')}</span>
          </motion.h1>

          {/* Sub-heading */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.7 }}
            className="text-lg sm:text-xl text-surface-400 max-w-2xl mx-auto mb-10 leading-relaxed"
          >
            {t('landing.heroSub')}
          </motion.p>

          {/* CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.6 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <Button
              onClick={() => navigate('/login')}
              className="px-8 py-3 text-base font-semibold"
            >
              <Play className="w-4 h-4" />
              {t('landing.ctaPrimary')}
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              {t('landing.ctaSecondary')}
              <ChevronDown className="w-4 h-4" />
            </Button>
          </motion.div>

          {/* Floating badges — decorative background layer */}
          <FloatingBadge className="top-8 -left-8 sm:left-4" delay={0}>
            <Bot className="w-3.5 h-3.5 text-brand-400" />
            <span className="text-surface-300">{t('landing.floatAi')}</span>
          </FloatingBadge>
          <FloatingBadge className="top-16 -right-8 sm:right-2" delay={0.2}>
            <Shield className="w-3.5 h-3.5 text-emerald-400" />
            <span className="text-surface-300">{t('landing.floatSecurity')}</span>
          </FloatingBadge>
          <FloatingBadge className="bottom-40 -left-8 sm:left-8" delay={0.4}>
            <Zap className="w-3.5 h-3.5 text-amber-400" />
            <span className="text-surface-300">{t('landing.floatFast')}</span>
          </FloatingBadge>
        </motion.div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2, duration: 1 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10"
        >
          <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.5 }}>
            <ChevronDown className="w-6 h-6 text-surface-500" />
          </motion.div>
        </motion.div>
      </section>

      {/* ═══ STATS BAR ════════════════════════════════════ */}
      <section className="relative z-10 border-y border-surface-800/50 bg-surface-900/50 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-6 py-12 grid grid-cols-2 md:grid-cols-4 gap-8">
          {stats.map((stat, i) => (
            <Reveal key={i} delay={i * 0.1} className="text-center">
              <div className="text-3xl sm:text-4xl font-extrabold text-surface-100">
                <Counter value={stat.value} suffix={stat.suffix} />
              </div>
              <div className="text-sm text-surface-500 mt-1">{stat.label}</div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ═══ FEATURES GRID ════════════════════════════════ */}
      <section id="features" className="relative py-24 sm:py-32">
        <div className="max-w-6xl mx-auto px-6">
          <Reveal className="text-center mb-16">
            <Badge variant="brand" size="lg" className="mb-4">
              {t('landing.featuresLabel')}
            </Badge>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-surface-100 mb-4">
              {t('landing.featuresTitle')}
            </h2>
            <p className="text-lg text-surface-400 max-w-2xl mx-auto">{t('landing.featuresSub')}</p>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feat, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div className="group relative p-6 rounded-2xl border border-surface-800/80 bg-surface-900/40 hover:border-brand-500/30 hover:bg-surface-900/70 transition-all duration-300 h-full">
                  {/* Gradient glow on hover */}
                  <div
                    className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${feat.color} opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-xl -z-10`}
                  />
                  <div
                    className={`w-12 h-12 rounded-xl bg-gradient-to-br ${feat.color} flex items-center justify-center mb-4`}
                  >
                    <feat.icon className={`w-6 h-6 ${feat.iconColor}`} />
                  </div>
                  <h3 className="text-lg font-semibold text-surface-100 mb-2 group-hover:text-surface-0 transition-colors">
                    {feat.title}
                  </h3>
                  <p className="text-sm text-surface-400 leading-relaxed">{feat.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ HOW IT WORKS ═════════════════════════════════ */}
      <section className="relative py-24 sm:py-32 overflow-hidden">
        {/* Background accent */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-brand-600/5 rounded-full blur-[120px]" />
        </div>

        <div className="max-w-5xl mx-auto px-6 relative z-10">
          <Reveal className="text-center mb-16">
            <Badge variant="success" size="lg" className="mb-4">
              {t('landing.howLabel')}
            </Badge>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-surface-100 mb-4">
              {t('landing.howTitle')}
            </h2>
            <p className="text-lg text-surface-400 max-w-2xl mx-auto">{t('landing.howSub')}</p>
          </Reveal>

          <div className="relative">
            {/* Vertical line */}
            <div className="absolute left-8 top-0 bottom-0 w-px bg-gradient-to-b from-brand-500/50 via-purple-500/50 to-transparent hidden md:block" />

            <div className="space-y-12">
              {steps.map((step, i) => (
                <Reveal key={i} delay={i * 0.15}>
                  <div className="flex items-start gap-6 md:gap-8">
                    {/* Step number */}
                    <div className="relative flex-shrink-0">
                      <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-brand-600/20 to-purple-600/20 border border-brand-500/20 flex items-center justify-center">
                        <step.icon className="w-7 h-7 text-brand-400" />
                      </div>
                      <span className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-brand-600 text-white text-xs font-bold flex items-center justify-center">
                        {i + 1}
                      </span>
                    </div>
                    {/* Content */}
                    <div className="pt-2">
                      <h3 className="text-xl font-semibold text-surface-100 mb-2">{step.title}</h3>
                      <p className="text-surface-400 leading-relaxed">{step.desc}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ═══ TECH STACK MARQUEE ═══════════════════════════ */}
      <section className="py-16 border-y border-surface-800/50 bg-surface-900/30">
        <Reveal className="text-center mb-10">
          <p className="text-sm font-medium text-surface-500 uppercase tracking-widest">
            {t('landing.builtWith')}
          </p>
        </Reveal>
        <div className="flex items-center justify-center gap-8 flex-wrap max-w-4xl mx-auto px-6">
          {techStack.map((tech, i) => (
            <Reveal key={i} delay={i * 0.07}>
              <div className="flex items-center gap-2 px-5 py-3 rounded-xl border border-surface-800 bg-surface-900/50 hover:border-surface-700 transition-colors">
                <tech.icon className="w-5 h-5 text-surface-400" />
                <span className="text-sm font-medium text-surface-300">{tech.label}</span>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ═══ AI SHOWCASE ══════════════════════════════════ */}
      <section className="relative py-24 sm:py-32">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <Reveal>
              <Badge variant="info" size="lg" className="mb-4">
                {t('landing.aiLabel')}
              </Badge>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-surface-100 mb-4">
                {t('landing.aiTitle')}
              </h2>
              <p className="text-surface-400 leading-relaxed mb-8">{t('landing.aiDesc')}</p>
              <div className="space-y-4">
                {[
                  t('landing.aiBullet1'),
                  t('landing.aiBullet2'),
                  t('landing.aiBullet3'),
                  t('landing.aiBullet4'),
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-brand-400 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-surface-300">{item}</span>
                  </div>
                ))}
              </div>
            </Reveal>

            {/* Code-like visual */}
            <Reveal delay={0.2}>
              <div className="relative">
                {/* Glow */}
                <div className="absolute -inset-4 bg-gradient-to-r from-brand-500/10 via-purple-500/10 to-pink-500/10 rounded-3xl blur-2xl" />
                <div className="relative rounded-2xl border border-surface-800 bg-surface-900/90 overflow-hidden">
                  {/* Title bar */}
                  <div className="flex items-center gap-2 px-4 py-3 border-b border-surface-800 bg-surface-900">
                    <div className="flex gap-1.5">
                      <div className="w-3 h-3 rounded-full bg-red-500/80" />
                      <div className="w-3 h-3 rounded-full bg-amber-500/80" />
                      <div className="w-3 h-3 rounded-full bg-emerald-500/80" />
                    </div>
                    <span className="text-xs text-surface-500 ml-2 font-mono">
                      arams-ai-agent.ts
                    </span>
                  </div>
                  {/* Code block */}
                  <div className="p-5 font-mono text-xs sm:text-sm leading-relaxed">
                    <div className="text-surface-500">
                      <span className="text-purple-400">{'// '}</span>
                      {t('landing.codeComment')}
                    </div>
                    <div className="mt-2">
                      <span className="text-purple-400">const</span>{' '}
                      <span className="text-surface-200">analysis</span>{' '}
                      <span className="text-surface-500">=</span>{' '}
                      <span className="text-purple-400">await</span>{' '}
                      <span className="text-brand-400">ai</span>
                      <span className="text-surface-500">.</span>
                      <span className="text-emerald-400">analyzeRepo</span>
                      <span className="text-surface-500">(</span>
                      <span className="text-amber-300">'my-app'</span>
                      <span className="text-surface-500">);</span>
                    </div>
                    <div className="mt-1">
                      <span className="text-purple-400">const</span>{' '}
                      <span className="text-surface-200">tests</span>{' '}
                      <span className="text-surface-500">=</span>{' '}
                      <span className="text-surface-200">analysis</span>
                      <span className="text-surface-500">.</span>
                      <span className="text-brand-400">suggestedTests</span>
                      <span className="text-surface-500">;</span>
                    </div>
                    <div className="mt-3 text-surface-500">
                      <span className="text-purple-400">{'// '}</span>
                      {t('landing.codeOutput')}
                    </div>
                    <div className="mt-1">
                      <span className="text-surface-500">{'→ '}</span>
                      <span className="text-emerald-400">25 tests</span>
                      <span className="text-surface-500">{' across '}</span>
                      <span className="text-amber-300">8 categories</span>
                    </div>
                    <div className="mt-1">
                      <span className="text-surface-500">{'→ '}</span>
                      <span className="text-emerald-400">18 roles</span>
                      <span className="text-surface-500">{' detected — '}</span>
                      <span className="text-pink-400">auth configured</span>
                    </div>
                    <div className="mt-1">
                      <span className="text-surface-500">{'→ '}</span>
                      <span className="text-brand-400">security checks</span>
                      <span className="text-surface-500">{' + '}</span>
                      <span className="text-amber-300">missing info</span>
                      <span className="text-surface-500">{' flagged'}</span>
                    </div>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ═══ FINAL CTA ════════════════════════════════════ */}
      <section className="relative py-24 sm:py-32">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-brand-600/10 rounded-full blur-[120px]" />
        </div>

        <Reveal className="relative z-10 max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-surface-100 mb-6">
            {t('landing.ctaTitle')}
          </h2>
          <p className="text-lg text-surface-400 mb-10 max-w-xl mx-auto">{t('landing.ctaDesc')}</p>
          <Button onClick={() => navigate('/login')} className="px-10 py-3 text-base font-semibold">
            <Rocket className="w-5 h-5" />
            {t('landing.ctaButton')}
          </Button>
        </Reveal>
      </section>

      {/* ═══ FOOTER ═══════════════════════════════════════ */}
      <footer className="border-t border-surface-800/50 bg-surface-900/30 py-8">
        <div className="max-w-6xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <img
              src="/arams-logo-white.png"
              alt="ARAMS"
              className="w-6 h-6 rounded-md object-contain"
            />
            <span className="text-sm font-semibold gradient-text">{t('common.appName')}</span>
          </div>
          <p className="text-xs text-surface-600">
            {t('common.copyright', { year: new Date().getFullYear() })}
          </p>
        </div>
      </footer>
    </div>
  );
}
